import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API, BACKEND_URL } from "@/App";
import BillingToggle from "../components/BillingToggle";
import PricingCard from "../components/PricingCard";

const DOWNLOAD_URL =
  "https://github.com/Converse-AI-Inc/Shadow/releases/latest/download/Shadow.dmg";

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;
const TEST_MODE = process.env.REACT_APP_PRICING_TEST_MODE === "true" || !RAZORPAY_KEY;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById("razorpay-checkout")) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-checkout";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const TIERS = [
  {
    name: "Starter",
    price: "Free",
    ctaLabel: "Download App",
    ctaVariant: "dark",
    blurb: "10 meetings per month",
    features: [
      "Real-time action detection",
      "Follow-up scheduling",
      "Creating docs on the fly",
      "Works with Zoom, Meet, Teams",
    ],
  },
  {
    name: "Pro",
    monthly: 25,
    annually: 20,
    ctaLabel: "Subscribe",
    ctaVariant: "dark",
    highlighted: true,
    blurb: "All essential features.",
    featuresLead: "Everything in Starter, plus...",
    features: [
      "Unlimited meeting duration",
      "Unlimited executions",
      "Slide creation, Notion docs, data pulls",
      "Priority support",
    ],
  },
  {
    name: "Enterprise",
    monthly: 50,
    annually: 40,
    ctaLabel: "Launching soon for enterprise",
    ctaVariant: "outline",
    blurb: "All essential features.",
    featuresLead: "Everything in Pro, plus...",
    features: [
      "SSO & advanced security",
      "Team-wide deployment",
      "Custom integrations",
      "Dedicated support",
    ],
  },
];

export default function PricingSection() {
  const { user, loading, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState("annually");
  const [subscribing, setSubscribing] = useState(false);
  const autoTriggeredRef = useRef(false);
  const isPro = user?.plan_tier === "pro";

  const handleSubscribe = useCallback(async () => {
    setSubscribing(true);
    try {
      const { data } = await axios.post(
        `${API}/pricing/subscribe`,
        {},
        { withCredentials: true }
      );

      if (TEST_MODE) {
        await axios.post(
          `${API}/pricing/verify`,
          {
            razorpay_payment_id: "test_pay_" + Date.now(),
            razorpay_subscription_id: data.razorpay_subscription_id,
            razorpay_signature: "test_sig_" + Date.now(),
          },
          { withCredentials: true }
        );
        await checkAuth();
        toast.success("Upgraded to Pro! (Test Mode)");
        setSubscribing(false);
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Failed to load payment gateway. Please try again.");
        setSubscribing(false);
        return;
      }

      const options = {
        key: RAZORPAY_KEY,
        subscription_id: data.razorpay_subscription_id,
        name: "ShadowLabs",
        description: `Pro Plan - ${billing === "annually" ? "Annual" : "Monthly"}`,
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/pricing/verify`,
              {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true }
            );
            await checkAuth();
            toast.success("Welcome to Pro! Your account has been upgraded.");
          } catch {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          email: user?.email || "",
          name: user?.name || "",
        },
        theme: { color: "#0E1430" },
        modal: { ondismiss: () => setSubscribing(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => setSubscribing(false));
      rzp.open();
    } catch (err) {
      setSubscribing(false);
      const msg = err.response?.data?.detail || "Failed to start subscription.";
      toast.error(typeof msg === "string" ? msg : msg.message || "Something went wrong.");
    }
  }, [user, billing, checkAuth]);

  // Returning from OAuth with ?upgrade=pro: auto-open Razorpay on this page.
  useEffect(() => {
    if (loading || !user || searchParams.get("upgrade") !== "pro" || autoTriggeredRef.current) {
      return;
    }
    autoTriggeredRef.current = true;
    setSearchParams({}, { replace: true });
    if (isPro) {
      toast.success("You're already on Pro.");
      return;
    }
    handleSubscribe();
  }, [user, loading, searchParams, setSearchParams, isPro, handleSubscribe]);

  const handleCtaClick = (tierName) => {
    if (tierName === "Starter") {
      window.open(DOWNLOAD_URL, "_blank", "noopener,noreferrer");
    } else if (tierName === "Pro") {
      if (subscribing) return;
      if (!user) {
        const next = "/?upgrade=pro";
        window.location.href = `${BACKEND_URL}/api/auth/google?next=${encodeURIComponent(next)}`;
      } else if (isPro) {
        toast.success("You're already on Pro.");
      } else {
        handleSubscribe();
      }
    }
    // Enterprise is disabled
  };

  return (
    <section id="pricing" className="relative scroll-mt-24 overflow-hidden isolate py-20 sm:py-28">
      <img
        src="/images/landing-v2/pricing/background.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-[clamp(1.6rem,3.5vw,2.5rem)] font-semibold tracking-tight text-neutral-900">
            Start for free. Scale when you're ready.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-neutral-500 sm:text-base">
            No bots. No setup. Just Shadow working in the background on every call.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <BillingToggle value={billing} onChange={setBilling} />
            <div className="flex items-center gap-1">
              <svg className="h-6 w-6 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              <span className="text-sm font-medium text-red-600" style={{ fontStyle: "italic", fontFamily: "cursive" }}>
                20% Off
              </span>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
          {TIERS.map((tier) => {
            const isPriced = typeof tier.monthly === "number";
            const amount = isPriced
              ? billing === "annually"
                ? tier.annually
                : tier.monthly
              : tier.price;
            return (
              <PricingCard
                key={tier.name}
                name={tier.name}
                price={isPriced ? `$${amount}` : amount}
                priceSuffix={isPriced ? `/month*` : null}
                ctaLabel={tier.ctaLabel}
                ctaVariant={tier.ctaVariant}
                highlighted={tier.highlighted}
                blurb={tier.blurb}
                featuresLead={tier.featuresLead}
                features={tier.features}
                onCtaClick={() => handleCtaClick(tier.name)}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
